// Function index:
//   renderAdminUsage(host) — fetch and render the per-user usage table,
//       showing tokens + USD spend breakdown by model and period.
//
// Period tabs (today / week / month / all-time) switch what data is shown.
// Table: rows = users (sorted by email), columns = models (sorted alpha) + total.
// Summary row at bottom shows aggregate tokens/USD per model across all users.

import { getAdminUsage, type Period, type AdminUsage } from '../../api.js';

export async function renderAdminUsage(host: HTMLElement): Promise<void> {
  host.innerHTML = 'Loading…';
  let currentPeriod: Period = 'today';

  const render = async (period: Period): Promise<void> => {
    currentPeriod = period;
    host.innerHTML = 'Loading…';
    try {
      const data = await getAdminUsage(period);
      host.innerHTML = '';
      host.appendChild(renderTabs(period, render));
      host.appendChild(renderTable(data));
    } catch (err) {
      host.innerHTML = '';
      const error = document.createElement('div');
      error.className = 'party-meta';
      error.textContent = `Could not load usage data: ${(err as Error).message}`;
      host.appendChild(error);
    }
  };

  await render(currentPeriod);
}

function renderTabs(
  currentPeriod: Period,
  onPeriodChange: (period: Period) => Promise<void>,
): HTMLDivElement {
  const container = document.createElement('div');
  container.className = 'admin-usage-tabs';

  const periods: Array<{ label: string; value: Period }> = [
    { label: 'Today', value: 'today' },
    { label: 'This Week', value: 'week' },
    { label: 'This Month', value: 'month' },
    { label: 'All Time', value: 'all' },
  ];

  for (const { label, value } of periods) {
    const btn = document.createElement('button');
    btn.dataset.period = value;
    btn.textContent = label;
    btn.className = value === currentPeriod ? 'admin-usage-tab active' : 'admin-usage-tab';
    btn.addEventListener('click', async () => {
      await onPeriodChange(value);
    });
    container.appendChild(btn);
  }

  return container;
}

function renderTable(data: AdminUsage): HTMLTableElement {
  const table = document.createElement('table');
  table.className = 'admin-usage-table';

  // Collect all unique models across all users, sorted alphabetically
  const models = new Set<string>();
  for (const user of data.users) {
    for (const model of Object.keys(user.tokens_by_model)) {
      models.add(model);
    }
  }
  const sortedModels = Array.from(models).sort();

  // Render thead
  const thead = document.createElement('thead');
  const headerRow = document.createElement('tr');

  const userHeader = document.createElement('th');
  userHeader.textContent = 'User';
  headerRow.appendChild(userHeader);

  for (const model of sortedModels) {
    const th = document.createElement('th');
    th.textContent = model.charAt(0).toUpperCase() + model.slice(1);
    headerRow.appendChild(th);
  }

  const totalHeader = document.createElement('th');
  totalHeader.textContent = 'Total USD';
  headerRow.appendChild(totalHeader);

  thead.appendChild(headerRow);
  table.appendChild(thead);

  // Render tbody
  const tbody = document.createElement('tbody');

  // Sort users by email
  const sortedUsers = [...data.users].sort((a, b) => a.email.localeCompare(b.email));

  for (const user of sortedUsers) {
    const row = document.createElement('tr');

    const userCell = document.createElement('td');
    const emailSpan = document.createElement('span');
    emailSpan.textContent = user.email;
    userCell.appendChild(emailSpan);

    const displaySpan = document.createElement('span');
    displaySpan.className = 'admin-usage-display-name';
    displaySpan.textContent = `· ${user.display_name}`;
    userCell.appendChild(displaySpan);

    row.appendChild(userCell);

    for (const model of sortedModels) {
      const cell = document.createElement('td');
      cell.className = 'admin-usage-data-cell';
      const modelData = user.tokens_by_model[model];
      const usdData = user.usd_by_model[model];
      if (modelData) {
        const totalTokens = modelData.input_tokens + modelData.output_tokens;
        const usdStr = (usdData ?? 0).toFixed(2);
        cell.textContent = `${totalTokens.toLocaleString()} tokens ($${usdStr})`;
      }
      row.appendChild(cell);
    }

    const totalCell = document.createElement('td');
    totalCell.className = 'admin-usage-total-cell';
    totalCell.textContent = `$${user.total_usd.toFixed(2)}`;
    row.appendChild(totalCell);

    tbody.appendChild(row);
  }

  // Summary row
  const summaryRow = document.createElement('tr');
  summaryRow.className = 'admin-usage-summary-row';

  const summaryLabel = document.createElement('td');
  summaryLabel.textContent = 'Total';
  summaryRow.appendChild(summaryLabel);

  // Calculate totals per model
  const modelTotals: Record<string, { tokens: number; usd: number }> = {};
  for (const model of sortedModels) {
    modelTotals[model] = { tokens: 0, usd: 0 };
  }
  for (const user of data.users) {
    for (const model of sortedModels) {
      const modelData = user.tokens_by_model[model];
      const usdData = user.usd_by_model[model];
      if (modelData) {
        const total = modelTotals[model]!;
        total.tokens += modelData.input_tokens + modelData.output_tokens;
      }
      if (usdData !== undefined) {
        const total = modelTotals[model]!;
        total.usd += usdData;
      }
    }
  }

  for (const model of sortedModels) {
    const cell = document.createElement('td');
    cell.className = 'admin-usage-summary-cell';
    const total = modelTotals[model]!;
    cell.textContent = `${total.tokens.toLocaleString()} tokens ($${total.usd.toFixed(2)})`;
    summaryRow.appendChild(cell);
  }

  const grandTotal = data.users.reduce((sum, u) => sum + u.total_usd, 0);
  const totalGrandCell = document.createElement('td');
  totalGrandCell.className = 'admin-usage-summary-cell';
  totalGrandCell.textContent = `$${grandTotal.toFixed(2)}`;
  summaryRow.appendChild(totalGrandCell);

  tbody.appendChild(summaryRow);
  table.appendChild(tbody);

  return table;
}
